import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { type ColDef } from "ag-grid-community";

import { ChartCard, EmptyState, KpiCard, KpiRow, MetricsGrid } from "./MetricsCards";
import {
  AXIS_PROPS,
  CHART_COLORS,
  Currency,
  LEGEND_PROPS,
  TOOLTIP_PROPS,
  downloadCsv,
  formatMonth,
  formatMoney,
  formatNumber,
  formatPercent,
} from "./metrics.utils";

import styles from "./CompanyMetricsTab.module.scss";

interface IMoney {
  charged: number;
  fees: number;
  refunded: number;
  refunded_fees: number;
  transactions: number;
  take_rate: number;
}

interface IMoneyRow extends IMoney {
  key: string;
}

interface IMoneyMonth extends IMoney {
  month: string;
}

interface ISubscriptionSegment {
  paying: number;
  trialing: number;
  canceled: number;
  mrr: number;
}

export interface IFinancialsData {
  totals: IMoney;
  previous: IMoney;
  by_month: IMoneyMonth[];
  by_sanctioning_club: IMoneyRow[];
  by_payment_method: IMoneyRow[];
  by_source: IMoneyRow[];
  subscriptions: {
    paying: number;
    trialing: number;
    canceled: number;
    mrr: number;
    trialing_mrr: number;
    dog_owners: ISubscriptionSegment;
    clubs: ISubscriptionSegment;
    by_status: { key: string; count: number }[];
    new_by_month: { month: string; count: number }[];
  };
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  card: "Card",
  bank_account: "Bank account",
  cash: "Cash",
  cheque: "Cheque",
  paypal: "PayPal",
  "e-transfer": "E-transfer",
  pay_club_directly: "Pay club directly",
  other: "Other",
};

const moneyCsvRows = (rows: IMoneyRow[], keyName: string) =>
  rows.map((r) => ({
    [keyName]: r.key,
    transactions: r.transactions,
    charged: r.charged,
    eager_dog_fees: r.fees,
    take_rate: (r.take_rate * 100).toFixed(2) + "%",
    refunded: r.refunded,
  }));

const MoneyTable: React.FC<{ rows: IMoneyRow[]; currency: Currency; keyLabel: string; labels?: Record<string, string> }> = ({ rows, currency, keyLabel, labels }) => {
  const columns: ColDef<IMoneyRow>[] = [
    { headerName: keyLabel, field: "key", minWidth: 180, valueFormatter: (p) => labels?.[p.value] ?? p.value },
    { headerName: "Transactions", field: "transactions", type: "rightAligned", valueFormatter: (p) => formatNumber(p.value) },
    { headerName: "Charged", field: "charged", type: "rightAligned", sort: "desc", valueFormatter: (p) => formatMoney(p.value, currency) },
    { headerName: "Eager Dog fees", field: "fees", type: "rightAligned", valueFormatter: (p) => formatMoney(p.value, currency) },
    { headerName: "Take rate", field: "take_rate", type: "rightAligned", valueFormatter: (p) => formatPercent(p.value) },
  ];
  return <MetricsGrid rows={rows} columns={columns} />;
};

const FinancialsPanel: React.FC<{ data: IFinancialsData; currency: Currency }> = ({ data, currency }) => {
  const { totals, previous, subscriptions } = data;
  const byMonth = data.by_month.map((r) => ({ ...r, label: formatMonth(r.month), take_rate_pct: Number((r.take_rate * 100).toFixed(2)) }));
  const money = (v: number) => formatMoney(v, currency);

  return (
    <div>
      <KpiRow>
        <KpiCard label="Total charged" value={money(totals.charged)} current={totals.charged} previous={previous.charged} />
        <KpiCard label="Eager Dog fees collected" value={money(totals.fees)} current={totals.fees} previous={previous.fees} />
        <KpiCard
          label="Average take rate"
          value={formatPercent(totals.take_rate)}
          hint={`Previous period: ${formatPercent(previous.take_rate)}`}
        />
        <KpiCard
          label="Refunded"
          value={money(totals.refunded)}
          hint={`${money(totals.refunded_fees)} in fees returned · ${formatNumber(totals.transactions)} transactions`}
        />
      </KpiRow>

      <div className={styles.grid}>
        {[
          { key: "charged", title: "Total charged per month", name: "Charged", color: CHART_COLORS[1] },
          { key: "fees", title: "Eager Dog fees per month", name: "Eager Dog fees", color: CHART_COLORS[0] },
        ].map((series) => (
          <ChartCard
            key={series.key}
            wide
            title={series.title}
            onExport={() => downloadCsv("financials-per-month", data.by_month.map((r) => ({ month: r.month, transactions: r.transactions, charged: r.charged, eager_dog_fees: r.fees, take_rate: (r.take_rate * 100).toFixed(2) + "%", refunded: r.refunded })))}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byMonth}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" {...AXIS_PROPS} />
                <YAxis {...AXIS_PROPS} tickFormatter={(v: number) => formatMoney(v, currency, true)} width={80} />
                <Tooltip {...TOOLTIP_PROPS} formatter={(v: number) => money(v)} />
                <Bar dataKey={series.key} name={series.name} fill={series.color} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        ))}

        <ChartCard title="Take rate per month" subtitle="Eager Dog fees ÷ total charged">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={byMonth}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} tickFormatter={(v: number) => `${v}%`} />
              <Tooltip {...TOOLTIP_PROPS} formatter={(v: number) => `${v}%`} />
              <Line type="monotone" dataKey="take_rate_pct" name="Take rate" stroke={CHART_COLORS[0]} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Events vs courses"
          onExport={() => downloadCsv("financials-by-source", moneyCsvRows(data.by_source, "source"))}
        >
          {data.by_source.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.by_source} dataKey="charged" nameKey="key" innerRadius={55} outerRadius={95} label={(e: IMoneyRow) => e.key}>
                  {data.by_source.map((r, i) => <Cell key={r.key} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip {...TOOLTIP_PROPS} formatter={(v: number) => money(v)} />
                <Legend {...LEGEND_PROPS} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </ChartCard>

        <ChartCard
          wide
          title="By sanctioning club"
          onExport={() => downloadCsv("financials-by-sanctioning-club", moneyCsvRows(data.by_sanctioning_club, "sanctioning_club"))}
        >
          <MoneyTable rows={data.by_sanctioning_club} currency={currency} keyLabel="Sanctioning club" />
        </ChartCard>

        <ChartCard
          wide
          title="By payment method"
          onExport={() => downloadCsv("financials-by-payment-method", moneyCsvRows(data.by_payment_method, "payment_method"))}
        >
          <MoneyTable rows={data.by_payment_method} currency={currency} keyLabel="Payment method" labels={PAYMENT_METHOD_LABELS} />
        </ChartCard>

        <ChartCard
          wide
          title="Subscriptions"
          subtitle="All subscriptions are billed in CAD, so these numbers don't change with the currency filter. Paying = active or past due."
          onExport={() => downloadCsv("subscriptions-new-per-month", subscriptions.new_by_month)}
        >
          <KpiRow>
            <KpiCard
              label="Dog owner subscribers (retired Nov 2026)"
              value={formatNumber(subscriptions.dog_owners.paying)}
              hint={`${formatNumber(subscriptions.dog_owners.trialing)} trialing · ${formatNumber(subscriptions.dog_owners.canceled)} canceled · Dog Owner Premium is no longer sold`}
            />
            <KpiCard
              label="Club subscribers"
              value={formatNumber(subscriptions.clubs.paying)}
              hint={`${formatNumber(subscriptions.clubs.trialing)} trialing · ${formatNumber(subscriptions.clubs.canceled)} canceled`}
            />
            <KpiCard
              label="MRR"
              value={formatMoney(subscriptions.mrr, "cad")}
              hint={`${formatMoney(subscriptions.trialing_mrr, "cad")} more if trials convert`}
            />
          </KpiRow>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={subscriptions.new_by_month.map((r) => ({ ...r, label: formatMonth(r.month) }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} allowDecimals={false} />
              <Tooltip {...TOOLTIP_PROPS} />
              <Bar dataKey="count" name="New subscriptions" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default FinancialsPanel;
